import React, { useState } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import { EllipsisVertical } from "lucide-react";
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import ScrollArea from "../ui/shared/ScrollArea";
import { useAssistants } from "@/hooks/useAssistants";
import { usePipelines } from "@/hooks/usePipelines";
import { toast } from "sonner";
import CampaignTagSelector from "./CampaignTagSelector";
import DistributeEquallySelection from "./DistributeEquallySelection";
import { useTeam } from "@/hooks/useTeam";
import { useTranslation } from "react-i18next";

// Define AccountCampaign type directly to avoid Database type conflicts
type AccountCampaign = {
  id: string
  account_id: string
  name: string
  assistant_id: string | null
  trigger_type: string
  trigger_keywords: string[] | null
  tags: string[] | null
  pipeline_id: string | null
  pipeline_stage_id: string | null
  is_active: boolean | null
  distribution_member_ids: string[] | null
  last_distribution_index: number | null
  created_at: string | null
  updated_at: string | null
}

type CampaignUpdate = Partial<Omit<AccountCampaign, 'id' | 'account_id' | 'created_at' | 'updated_at'>>

interface CampaignEditProps {
  campaign: AccountCampaign;
  updateCampaign: (id: string, data: Partial<CampaignUpdate>) => Promise<boolean>;
  deleteCampaign: (id: string) => Promise<boolean>;
}

const CampaignEdit: React.FC<CampaignEditProps> = ({ campaign, updateCampaign, deleteCampaign }) => {
  const { t } = useTranslation("automations");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { assistants } = useAssistants();
  const { pipelines, pipelineStages } = usePipelines();
  const { members } = useTeam();

  const [formData, setFormData] = useState({
    name: campaign.name,
    assistant_id: campaign.assistant_id || '',
    trigger_type: campaign.trigger_type,
    trigger_keywords: campaign.trigger_keywords || [],
    tags: campaign.tags || [],
    pipeline_id: campaign.pipeline_id || '',
    pipeline_stage_id: campaign.pipeline_stage_id || '',
    is_active: campaign.is_active,
    distribution_member_ids: campaign.distribution_member_ids || [],
  });

  const [keywordInput, setKeywordInput] = useState('');

  const handleFormChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddKeyword = () => {
    if (keywordInput.trim() && !formData.trigger_keywords.includes(keywordInput.trim())) {
      setFormData(prev => ({
        ...prev,
        trigger_keywords: [...prev.trigger_keywords, keywordInput.trim()]
      }));
      setKeywordInput('');
    }
  };

  const handleRemoveKeyword = (keyword: string) => {
    setFormData(prev => ({
      ...prev,
      trigger_keywords: prev.trigger_keywords.filter((k: string) => k !== keyword)
    }));
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error(t("campaign.errName"));
      return;
    }

    if (!formData.trigger_type) {
      toast.error(t("campaign.errTrigger"));
      return;
    }

    if (formData.pipeline_id && formData.pipeline_id !== 'none' && (!formData.pipeline_stage_id || formData.pipeline_stage_id === 'none')) {
      toast.error(t("campaign.errStage"));
      return;
    }

    setLoading(true);

    try {
      const updateData = {
        name: formData.name,
        assistant_id: formData.assistant_id === 'none' ? null : formData.assistant_id || null,
        trigger_type: formData.trigger_type,
        trigger_keywords: formData.trigger_keywords,
        tags: formData.tags,
        pipeline_id: formData.pipeline_id === 'none' ? null : formData.pipeline_id || null,
        pipeline_stage_id: formData.pipeline_stage_id === 'none' ? null : formData.pipeline_stage_id || null,
        is_active: formData.is_active,
        distribution_member_ids: formData.distribution_member_ids.length > 0 ? formData.distribution_member_ids : null,
      };

      const success = await updateCampaign(campaign.id, updateData);

      if (success) {
        toast.success(t("campaign.updated"));
        setOpen(false);
      } else {
        toast.error(t("campaign.updateError"));
      }
    } catch (error) {
      console.error('Erro ao atualizar campanha:', error);
      toast.error(t("campaign.updateError"));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm(t("campaign.confirmDelete"))) {
      return;
    }

    setLoading(true);

    try {
      const success = await deleteCampaign(campaign.id);

      if (success) {
        toast.success(t("campaign.removed"));
        setOpen(false);
      } else {
        toast.error(t("campaign.removeError"));
      }
    } catch (error) {
      console.error('Erro ao deletar campanha:', error);
      toast.error(t("campaign.removeError"));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (newOpen) {
      setFormData({
        name: campaign.name,
        assistant_id: campaign.assistant_id || '',
        trigger_type: campaign.trigger_type,
        trigger_keywords: campaign.trigger_keywords || [],
        tags: campaign.tags || [],
        pipeline_id: campaign.pipeline_id || '',
        pipeline_stage_id: campaign.pipeline_stage_id || '',
        is_active: campaign.is_active,
        distribution_member_ids: campaign.distribution_member_ids || [],
      });
    }
  };

  // Filtrar stages baseado no pipeline selecionado
  const availableStages = pipelineStages.filter(stage => 
    stage.pipeline_id === formData.pipeline_id
  );

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button aria-label={t("campaign.actions")} className="w-[34px] h-[34px] flex justify-center items-center rounded-lg border border-[#E5E7EB] bg-white text-[#64748B] hover:bg-[#F1F5F9] hover:text-[#2563EB] transition-colors duration-200 dark:border-[#3F3F46] dark:bg-transparent dark:text-[#A1A1AA] dark:hover:bg-[#27272A] dark:hover:text-[#60A5FA]">
          <EllipsisVertical size={18} aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="!bg-white p-1 w-[95px] flex flex-col gap-1 border border-[#E5E7EB] rounded-xl shadow-md dark:!bg-[#27272A] dark:border-[#3F3F46] dark:shadow-none">
        <Dialog>
          <DialogTrigger asChild>
            <div className="w-full flex justify-center rounded-lg items-center bg-transparent hover:bg-[#F1F5F9] min-h-[29px] text-[#0F172A] transition-colors duration-200 dark:hover:bg-[#3F3F46] dark:text-[#FAFAFA]">
              {t("common.edit")}
            </div>
          </DialogTrigger>
          <DialogContent className="flex !max-w-[600px] w-full p-0 rounded-2xl border border-[#E5E7EB] bg-white text-[#0F172A] flex-col dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA]">
            <ScrollArea classNames={{ root: "max-h-[90dvh] p-5 overflow-auto" }}>
              <div className="w-full flex flex-col gap-4">
                <DialogHeader>
                  <DialogTitle className="text-lg font-semibold text-[#0F172A] dark:text-[#FAFAFA]">{t("campaign.editTitle")}</DialogTitle>
                </DialogHeader>

                <form className="w-full flex flex-col gap-4">
                  {/* Nome */}
                  <div className="flex flex-col w-full items-start gap-3">
                    <Label htmlFor="name" className="font-medium text-[#0F172A] dark:text-[#FAFAFA]">{t("campaign.nameRequired")}</Label>
                    <Input
                      placeholder={t("campaign.namePlaceholder")}
                      id="name"
                      value={formData.name}
                      onChange={(e) => handleFormChange('name', e.target.value)}
                      className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] focus-visible:ring-[#2563EB]/20 dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A] dark:focus-visible:border-[#60A5FA] text-[14px] min-h-[36px]"
                      type="text"
                      required
                    />
                  </div>

                  {/* Assistente */}
                  <div className="flex flex-col w-full items-start gap-3">
                    <Label htmlFor="assistant" className="font-medium text-[#0F172A] dark:text-[#FAFAFA]">{t("common.assistant")}</Label>
                    <Select value={formData.assistant_id} onValueChange={(value) => handleFormChange('assistant_id', value)}>
                      <SelectTrigger className="h-11 !h-[40px] rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA] text-[14px] w-full">
                        <SelectValue placeholder={t("common.pickAssistant")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem value="none">{t("common.noAssistant")}</SelectItem>
                          {assistants.filter(a => a.is_active).map((assistant) => (
                            <SelectItem key={assistant.id} value={assistant.id}>
                              {assistant.name}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Tipo de Gatilho */}
                  <div className="flex flex-col w-full items-start gap-3">
                    <Label htmlFor="trigger_type" className="font-medium text-[#0F172A] dark:text-[#FAFAFA]">{t("campaign.triggerRequired")}</Label>
                    <Select value={formData.trigger_type} onValueChange={(value) => handleFormChange('trigger_type', value)}>
                      <SelectTrigger className="h-11 !h-[40px] rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA] text-[14px] w-full">
                        <SelectValue placeholder={t("campaign.pickTrigger")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem value="first_contact">{t("campaign.triggerFirst")}</SelectItem>
                          <SelectItem value="contains">{t("campaign.triggerContains")}</SelectItem>
                          <SelectItem value="starts_with">{t("campaign.triggerStarts")}</SelectItem>
                          <SelectItem value="any">{t("campaign.triggerAny")}</SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Palavras-chave */}
                  {formData.trigger_type !== 'any' && formData.trigger_type !== 'first_contact' && (
                    <div className="flex flex-col w-full items-start gap-3">
                      <Label htmlFor="keywords" className="font-medium text-[#0F172A] dark:text-[#FAFAFA]">{t("campaign.keywords")}</Label>
                      <div className="flex gap-2">
                        <Input
                          placeholder={t("campaign.keywordPlaceholder")}
                          value={keywordInput}
                          onChange={(e) => setKeywordInput(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddKeyword())}
                          className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] focus-visible:ring-[#2563EB]/20 dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A] dark:focus-visible:border-[#60A5FA] text-[14px] min-h-[36px]"
                          type="text"
                        />
                        <Button type="button" onClick={handleAddKeyword} className="min-w-[80px] rounded-xl bg-[#2563EB] text-white hover:bg-[#1D4ED8] transition-colors duration-200">
                          {t("common.add")}
                        </Button>
                      </div>
                      {formData.trigger_keywords.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {formData.trigger_keywords.map((keyword: string, index: number) => (
                            <div key={index} className="flex items-center gap-2 rounded-full px-2.5 py-1 bg-[#DBEAFE] text-[#2563EB] dark:bg-blue-500/20 dark:text-[#60A5FA]">
                              <span className="text-sm">{keyword}</span>
                              <button
                                type="button"
                                onClick={() => handleRemoveKeyword(keyword)}
                                className="text-[#64748B] hover:text-[#DC2626] transition-colors duration-200 dark:text-[#A1A1AA] dark:hover:text-[#F87171]"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Pipeline */}
                  <div className="flex flex-col w-full items-start gap-3">
                    <Label htmlFor="pipeline" className="font-medium text-[#0F172A] dark:text-[#FAFAFA]">{t("common.pipeline")}</Label>
                    <Select 
                      value={formData.pipeline_id} 
                      onValueChange={(value) => {
                        handleFormChange('pipeline_id', value);
                        handleFormChange('pipeline_stage_id', ''); // Reset stage when pipeline changes
                      }}
                    >
                      <SelectTrigger className="h-11 !h-[40px] rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA] text-[14px] w-full">
                        <SelectValue placeholder={t("common.pickPipeline")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem value="none">{t("common.noPipeline")}</SelectItem>
                          {pipelines.map((pipeline) => (
                            <SelectItem key={pipeline.id} value={pipeline.id}>
                              {pipeline.name}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Etapa do Pipeline */}
                  {formData.pipeline_id && formData.pipeline_id !== 'none' && (
                    <div className="flex flex-col w-full items-start gap-3">
                      <Label htmlFor="pipeline_stage" className="font-medium text-[#0F172A] dark:text-[#FAFAFA]">{t("common.stageRequired")}</Label>
                      <Select value={formData.pipeline_stage_id} onValueChange={(value) => handleFormChange('pipeline_stage_id', value)}>
                        <SelectTrigger className="h-11 !h-[40px] rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA] text-[14px] w-full">
                          <SelectValue placeholder={t("common.pickStage")} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            <SelectItem value="none">{t("common.noStage")}</SelectItem>
                            {availableStages.map((stage) => (
                              <SelectItem key={stage.id} value={stage.id}>
                                {stage.name}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Tags */}
                  <CampaignTagSelector
                    selectedTags={formData.tags}
                    onTagsChange={(tags) => handleFormChange('tags', tags)}
                  />

                  {/* Distribuição de Leads */}
                  <div className="flex flex-col w-full items-start gap-3">
                    <Label className="font-medium text-[#0F172A] dark:text-[#FAFAFA]">{t("campaign.distribution")}</Label>
                    <p className="text-xs text-[#64748B] dark:text-[#71717A] -mt-1">
                      {t("campaign.distributionHint")}
                    </p>
                    <DistributeEquallySelection
                      options={members.filter(m => m.is_active)}
                      selectedIds={formData.distribution_member_ids}
                      onSelectionChange={(ids) => handleFormChange('distribution_member_ids', ids)}
                      placeholder={t("campaign.pickMembers")}
                    />
                  </div>

                  <div className="w-full flex justify-end gap-3 mt-4">
                    <Button 
                      type="button"
                      onClick={handleSave}
                      disabled={loading}
                      className="min-w-[120px] rounded-xl bg-[#2563EB] text-white shadow-sm hover:bg-[#1D4ED8] transition-colors duration-200 dark:bg-[#2563EB] dark:hover:bg-[#1D4ED8]"
                    >
                      {loading ? 'Salvando...' : 'Atualizar'}
                    </Button>
                  </div>
                </form>
              </div>
            </ScrollArea>
          </DialogContent>
        </Dialog>
        <button 
          onClick={handleDelete}
          disabled={loading}
          className="w-full flex justify-center rounded-lg items-center bg-transparent hover:bg-[#FEF2F2] min-h-[29px] text-[#DC2626] transition-colors duration-200 dark:hover:bg-red-500/10 dark:text-[#F87171]"
        >
          {loading ? t('common:shared.removing') : t('campaign.remove')}
        </button>
      </PopoverContent>
    </Popover>
  );
};

export default CampaignEdit;